import { sodApi } from '@/services/index'
import { EvidenceType } from '../types';

export type AppropriatenessStatusType = {
  id: string
  label: string;
  description: string;
};

export type AppropriatenessListItem = {
  id: string;
  applicationName: string;
  engagementId: string;
  description: string;
  statusType: number;
  signOffDueDate: string;
  createdOn: string;
  updatedOn: string;
  createdBy: string;
  updatedBy: string;
  userName: string;
  serviceAccount: string;
  userId: string;
  userEmail: string;
  jobTitle: string;
  environment: string;
  department: string;
  userPermission: string;
  hrMatchMessage?: string;
}

export type AppropriatenessListResponse = AppropriatenessListItem[];

export type AppropriatenessJobStatus = {
  /** null = never run; 0 = NotStarted (queued); 1 = InProgress (truly running in AZF); 6 = Completed; 7 = Failed; 8 = Submitted (awaiting async AI/LLM callback) */
  jobStatusId: number | null
  /** Id of the JobRecord this status was computed from. Null = no job has ever been created. */
  jobId: string | null
  isInProgress: boolean
  /** InProgress but last activity > 10 min ago */
  isStale: boolean
  updatedOn: string | null
  /** "no_evidence" | "evidence_not_verified" | "too_many_failures" | null */
  blockedReason: string | null
  /** Existing saved rows are stale because newer evidence activity exists */
  requiresRefresh: boolean
  /** HR document indexing is in progress for this engagement */
  isHRIndexing: boolean
}

export const AppropriateApi = sodApi.injectEndpoints({
  endpoints: (builder) => ({
    listAppropriateness: builder.query<
      AppropriatenessListResponse,
      {
        applicationId: string
        type: EvidenceType;
        filter?: string[]
      }
    >({
      query: ({ applicationId, type, filter }) => {
        const params: Record<string, unknown> = {}
        params.type = type
        if (filter && filter.length > 0) {
          params.filter = filter
        }
        return {
          url: `/Appropriateness/${applicationId}/List`,
          method: 'GET',
          params,
        }
      }
    }),
    updateAppropriateness: builder.mutation<any, any>({
      query: (body) => ({
        url: '/Appropriateness/Update',
        method: 'POST',
        body,
        contentType: 'application/json',
      }),
    }),
    updateAppropriatenessFields: builder.mutation<
      void,
      { appropriatenessId: string } & Partial<{
        userName: string;
        serviceAccount: string;
        userId: string;
        userEmail: string;
        jobTitle: string;
        environment: string;
        department: string;
        userPermission: string;
      }>
    >({
      query: ({ appropriatenessId, ...fields }) => ({
        url: `/Appropriateness/${appropriatenessId}/fields`,
        method: 'PATCH',
        body: fields,
        contentType: 'application/json',
      }),
    }),
    getAppropriatenessStatusList: builder.query<AppropriatenessStatusType[], void>({
      query: () => ({
        url: '/Appropriateness/StatusList',
        method: 'GET',
      }), 
    }),
    getAppropriatenessJobStatus: builder.query<
      AppropriatenessJobStatus,
      { applicationId: string; type: EvidenceType }
    >({
      query: ({ applicationId, type }) => ({
        url: `/Appropriateness/${applicationId}/${type}/jobStatus`,
        method: 'GET',
      }),
    }),
    startAppropriatenessPipeline: builder.mutation<
      { applicationId: string; jobId: string },
      { applicationId: string; type: EvidenceType }
    >({
      query: ({ applicationId, type }) => ({
        url: `/Appropriateness/${applicationId}/${type}/start`,
        method: 'GET',
      }),
    }),
    resetAppropriateness: builder.mutation<
      void,
      { applicationId: string; type: EvidenceType }
    >({
      query: ({ applicationId, type }) => ({
        url: `/Appropriateness/${applicationId}/reset`,
        method: 'DELETE',
        params: { type },
      }),
    }),
  }),
})

export const {
  useListAppropriatenessQuery,
  useUpdateAppropriatenessMutation,
  useGetAppropriatenessStatusListQuery,
  useUpdateAppropriatenessFieldsMutation,
  useGetAppropriatenessJobStatusQuery,
  useStartAppropriatenessPipelineMutation,
  useResetAppropriatenessMutation,
} = AppropriateApi
